import * as React from "react";
import Modal from "../../misc/components/modal";
import Alert, { AlertCategoryEnum } from "../../misc/components/Alert";
import { useWebPortalApiClient, useWebPortalLegacyClient } from "../../misc/api/WebPortalTokenClient";
import { MultiSelect } from "../../forms/informedv2/MultiSelect";
import { Option } from "../../Components/MatchSortHeader";
import { OrderDirection, Package, VitalographUpdateClient } from "@vitalograph/update-client";
import { useAboutContextProvider } from "../../Core/AboutContextProvider";
import { uniqBy } from "../../misc/ArrayFunctions";
import { Form, useFormApi, useFormState } from "informed";
import { nameof } from "ts-simple-nameof";
import { Country, Site, Study } from "webportalclient";
import { Required } from "../../forms/validation/Required";
import LoadingWithMessage from "../../misc/components/LoadingWithMessage";
import { FormattedPackage } from "./DeviceForm";

interface UpdateDevicesVersions {
    onClose: () => void;
}

interface UpdateDevicesVersionFromModel {
    countries: Country[],
    study: Study,
    sites: Site[]
    version: Package
}

export const UpdateDevicesVersionsFrom = ({ onClose }: UpdateDevicesVersions) => {
    const [error, setError] = React.useState<string | undefined>();
    const client = useWebPortalApiClient();
    const legacyClient = useWebPortalLegacyClient();
    const { about } = useAboutContextProvider();
    const [updateClient, setUpdateClient] = React.useState(new VitalographUpdateClient(about.UpdateServiceUri));
    const [packages, setPackages] = React.useState<FormattedPackage[]>([]);

    const [studies, setStudies] = React.useState<Study[]>();
    const [sites, setSites] = React.useState<Site[]>([]);
    const [allSites, setAllSites] = React.useState<Site[]>();
    const [countries, setCountries] = React.useState<Country[]>();
    const [allCountries, setAllCountries] = React.useState<Country[]>();

    const formApi = useFormApi<UpdateDevicesVersionFromModel>();
    const formState = useFormState<UpdateDevicesVersionFromModel>();
    React.useEffect(() => {
        const getDropDowns = async () => {
            const studies = await client.client.getStudies("id,name", undefined, "name");
            setStudies(studies.value);

            const sites = await client.client.getSites("id,number,country,studyId", undefined, "number");
            setSites(sites.value);
            setAllSites(sites.value);

            const countriesFilter = "id in (" + uniqBy(sites.value, (a, b) => a.country == b.country).map(e => `"${e.country}"`).join(',') + ")";
            const countries = await client.client.getCountries("id,name", countriesFilter, "name");
            setCountries(countries.value);
            setAllCountries(countries.value);



        };

        getDropDowns();
    }, []);

    const onSaveClick = async () => {
        formApi.validate()
        const state = formApi.getState();
        if (state.invalid)
            return;
        try {
            await legacyClient.patchDevicesSpirotracVersion({
                Countries: state.values.countries?.map(e => e.id) ?? [],
                Sites: state.values.sites?.map(e => e.id) ?? [],
                Studies: state.values.study ? [state.values.study.id] : [],
                Version: state.values.version.version
            })
            setError(undefined);
            onClose();
        } catch (error) {
            setError(error);
        }

    }
    const onStudiesChanges = async () => {
        const state = formApi.getState();
        if (state.values.study) {
            const filteredSites = allSites.filter(e => formState.values.study.id == e.studyId && (!formState.values.countries || formState.values.countries.some(f => f.id == e.country)));
            setSites(filteredSites)
            setCountries(allCountries.filter(e => filteredSites.some(r => r.country == e.id)))

            const packageName = await legacyClient.getSetting("spirotrac6.configuration.packageName", formState.values.study.id);
            const packagesResult = await updateClient.package_PostPackages({
                filters: [
                    {
                        member: "Name",
                        values: [
                            packageName
                        ],
                    }
                ],
                order: {
                    member: "Version",
                    direction: OrderDirection.Descending
                }
            });
            const packageOptions = packagesResult.data.map(p => {
                const formatted = p as FormattedPackage;
                formatted.displayValue = p.name + "." + p.version;
                return formatted;
            })
            setPackages(packageOptions)
        } else {
            setSites([]);
            setCountries([]);
            setPackages([]);
        }
    }

    const onCountryChanges = async () => {
        const state = formApi.getState();
        if (state.values.countries) {
            const filteredSites = allSites.filter(e => formState.values.countries.some(f => f.id == e.country) && (!state.values.study || formState.values.study.id == e.studyId));
            setSites(filteredSites)
        } else {
            setSites(allSites);
            setCountries(allCountries);
            setPackages([]);
        }
    }
    if (!studies || !countries || !sites)
        return <LoadingWithMessage message="Loading ..." />;
    return (
        <>
            <div className="row">
                <div className="col-md-12">
                    <div className="panel-body">
                        {error ? <Alert show={true} category={AlertCategoryEnum.Danger}>{error}</Alert> : null}
                        <div className="form-group col-md-6 col-sm-6 col-xs-12">
                            <label htmlFor="AllocatedSite">Studies</label>
                            <MultiSelect
                                disabled={!studies.length}
                                id="Studies"
                                field={nameof<UpdateDevicesVersionFromModel>(f => f.study)}
                                options={studies}
                                labelKey="name"
                                valueKey="id"
                                multi={false}
                                onChange={onStudiesChanges}
                                validate={Required}
                            />
                        </div>
                        <div className="form-group col-md-6 col-sm-6 col-xs-12">
                            <label htmlFor="Countries">Countries</label>
                            <MultiSelect
                                disabled={!countries.length}
                                id="Countries"
                                field={nameof<UpdateDevicesVersionFromModel>(f => f.countries)}
                                options={countries}
                                labelKey="name"
                                valueKey="id"
                                multi={true}
                                onChange={onCountryChanges}
                            />
                        </div>
                        <div className="form-group col-md-6 col-sm-6 col-xs-12">
                            <label htmlFor="AllocatedSite">Sites</label>
                            <MultiSelect
                                disabled={!sites.length}
                                id="AllocatedSite"
                                field={nameof<UpdateDevicesVersionFromModel>(f => f.sites)}
                                options={sites}
                                labelKey="number"
                                valueKey="id"
                                multi={true}
                            />
                        </div>
                        <div className="form-group col-md-6 col-sm-6 col-xs-12">
                            <label htmlFor="spirotracPackageVersion">Package Version</label>
                            <MultiSelect
                                disabled={!packages.length}
                                id="spirotracPackageVersion"
                                field={nameof<UpdateDevicesVersionFromModel>(f => f.version)}
                                options={packages}
                                labelKey="displayValue"
                                valueKey="version"
                                multi={false}
                                validate={Required}
                            />
                        </div>
                    </div>
                </div>
            </div>
            <div className="modal-footer">
                <button id="updateButton" className="btn btn-primary" type="submit" disabled={false} onClick={onSaveClick}>
                    Update
                </button>&nbsp;
                <button id="returnToListButton" className="btn btn-primary btn-outline" onClick={onClose}>
                    Return to List
                </button>
            </div>
        </>
    );
}


export const UpdateDevicesVersions = ({ onClose }: UpdateDevicesVersions) => {
    return (
        <Modal show={true} header="Update Devices Package Version" closeModalHandler={onClose}>
            <Form<UpdateDevicesVersionFromModel>>
                <UpdateDevicesVersionsFrom onClose={onClose} />
            </Form>
        </Modal>
    )

}